import {CommonModule} from '@angular/common';
import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {debounceTime, distinctUntilChanged, Subject, Subscription, switchMap} from 'rxjs';
import {TableLazyLoadEvent, TableModule} from 'primeng/table';
import {DatePicker} from 'primeng/datepicker';
import {MultiSelect} from 'primeng/multiselect';
import {InputText} from 'primeng/inputtext';
import {Button} from 'primeng/button';
import {Skeleton} from 'primeng/skeleton';
import {Checkbox} from 'primeng/checkbox';
import {Dialog} from 'primeng/dialog';
import {AbrirSei} from '@components/commons/abrir-sei/abrir-sei';
import {MessageService} from 'primeng/api';
import {IconBoldBroom} from '@components/icons/icon-bold-broom/icon-bold-broom';
import {EnumsService} from '@core/services/enums.service';
import {EnumResponse} from '@core/models/enum-response';
import {OpcoesFiltroVerificacao, VerificacaoService} from '@core/services/verificacao.service';
import {LinhaTempo, NotificacaoVerificacao} from '@core/models/verificacao';
import {PageableParams} from '@core/models/pageable-params';
import {RowsPerPageOptions} from '@core/models/page-options';
import {DateUtils} from '@shared/utils/date.utils';

@Component({
  selector: 'app-verificar-dados',
  imports: [
    CommonModule, ReactiveFormsModule, FormsModule, TableModule, DatePicker, MultiSelect,
    InputText, Button, Skeleton, IconBoldBroom, Checkbox, Dialog, AbrirSei,
  ],
  templateUrl: './verificar-dados.html',
  styleUrl: './verificar-dados.css',
})
export class VerificarDados implements OnInit, OnDestroy {

  protected loading = false;
  protected dados: NotificacaoVerificacao[] = [];
  protected totalRecords = 0;
  protected first = 0;
  protected rows = 10;
  protected readonly rowsOptions = RowsPerPageOptions;
  protected filterForm!: FormGroup;

  protected statusOptions: EnumResponse[] = [];
  protected faseOptions: EnumResponse[] = [];
  protected superintendenciaOptions: EnumResponse[] = [];
  protected concessionariaOptions: EnumResponse[] = [];

  // "Exibição de Colunas" / "Exibir Paginação" / filtros por coluna — mesmo padrão da
  // tela Controle de Notificações, mas persistido em localStorage (tela sem preferências
  // no servidor).
  protected readonly allColumns: { field: string; label: string }[] = [
    {field: 'numeroNotificacao', label: 'Nº NOT'},
    {field: 'processo', label: 'Nº SEI'},
    {field: 'concessionaria', label: 'Concessionária'},
    {field: 'superintendencia', label: 'Superintendência'},
    {field: 'dataNotificacao', label: 'Data NOT'},
    {field: 'statusLinhaTempo', label: 'Linha do tempo — Status'},
    {field: 'faseLinhaTempo', label: 'Linha do tempo — Fase'},
  ];
  protected visibleFields = new Set<string>(this.allColumns.map(c => c.field));
  protected configColumns: { field: string; label: string; visible: boolean }[] = [];
  protected showColumnsDialog = false;
  protected showColumnFilters = true;
  protected showPagination = true;

  private enumsService = inject(EnumsService);
  private service = inject(VerificacaoService);
  private fb = inject(FormBuilder);
  private messageService = inject(MessageService);
  private lastSort?: { field: string; order: number };
  private reload$ = new Subject<void>();
  private subs = new Subscription();

  ngOnInit() {
    this.filterForm = this.fb.group({
      periodo: [null],
      statusLinhaTempo: [null],
      faseLinhaTempo: [null],
      superintendencia: [null],
      concessionaria: [null],
      numeroNotificacao: [''],
      processo: [''],
    });

    this.enumsService.initEnums().then(() => {
      this.statusOptions = [...this.enumsService.status];
      this.faseOptions = [...this.enumsService.fases];
      this.superintendenciaOptions = [...this.enumsService.superintendencias];
      this.concessionariaOptions = [...this.enumsService.concessionarias];
      this.service.getOpcoesFiltro(this.buildFilters()).subscribe({
        next: opcoes => this.aplicarOpcoesFiltro(opcoes),
        error: () => {
        },
      });
    });

    this.loadPreferencias();

    // debounce colapsa o double-fire (chamada explícita + onLazyLoad disparado pelo binding [first])
    this.subs.add(this.reload$.pipe(debounceTime(250)).subscribe(() => this.load()));

    this.subs.add(
      this.filterForm.valueChanges
        .pipe(
          debounceTime(400),
          distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        )
        .subscribe(() => {
          this.first = 0;
          this.reload$.next();
        }),
    );

    // Filtros em cascata: refaz as opções considerando os OUTROS filtros já ativos, e
    // remove da seleção o que sumiu. Assinatura própria (mesmo debounce) pra poder usar
    // switchMap e cancelar uma busca de opções desatualizada.
    this.subs.add(
      this.filterForm.valueChanges
        .pipe(
          debounceTime(400),
          distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
          switchMap(() => this.service.getOpcoesFiltro(this.buildFilters())),
        )
        .subscribe({
          next: opcoes => this.aplicarOpcoesFiltro(opcoes),
          error: () => {
          },
        }),
    );
  }

  private aplicarOpcoesFiltro(opcoes: OpcoesFiltroVerificacao) {
    this.statusOptions = this.enumsService.status.filter(o => opcoes.status?.includes(o.nome));
    this.faseOptions = this.enumsService.fases.filter(o => opcoes.fase?.includes(o.nome));
    this.superintendenciaOptions = this.enumsService.superintendencias
        .filter(o => opcoes.superintendencia?.includes(o.nome));
    this.concessionariaOptions = this.enumsService.concessionarias
        .filter(o => opcoes.concessionaria?.includes(o.nome));

    this.removerSelecaoObsoleta('statusLinhaTempo', opcoes.status);
    this.removerSelecaoObsoleta('faseLinhaTempo', opcoes.fase);
    this.removerSelecaoObsoleta('superintendencia', opcoes.superintendencia);
    this.removerSelecaoObsoleta('concessionaria', opcoes.concessionaria);
  }

  /** Tira da seleção do campo os valores que não estão mais entre as opções disponíveis. */
  private removerSelecaoObsoleta(campo: string, disponiveis: string[] | undefined) {
    const control = this.filterForm?.get(campo);
    if (!control || !disponiveis) return;
    const atual: string[] = control.value ?? [];
    if (!Array.isArray(atual) || atual.length === 0) return;
    const sobreviventes = atual.filter(v => disponiveis.includes(v));
    if (sobreviventes.length !== atual.length) {
      control.setValue(sobreviventes.length ? sobreviventes : null);
    }
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  protected onLazyLoad(event: TableLazyLoadEvent) {
    this.first = event.first ?? 0;
    this.rows = event.rows ?? this.rows;
    if (event.sortField) {
      this.lastSort = {field: event.sortField as string, order: event.sortOrder ?? 1};
    }
    this.reload$.next();
  }

  protected limparFiltros() {
    // Não depende do valueChanges (p-multi-select às vezes reemite [] em vez de null,
    // o que pode enganar o distinctUntilChanged): zera e recarrega explicitamente.
    this.filterForm.reset({}, {emitEvent: false});
    this.first = 0;
    this.reload$.next();
  }

  protected get temFiltros(): boolean {
    if (!this.filterForm) return false;
    // Não usa PageableParams.areFiltersEmpty aqui: ela não trata array vazio ([] de um
    // p-multi-select sem seleção) como "vazio", e por isso o botão Limpar Filtros
    // aparecia sempre habilitado.
    return Object.values(this.filterForm.value).some((val: unknown) => {
      if (Array.isArray(val)) return val.length > 0;
      return val !== null && val !== undefined && val !== '';
    });
  }

  protected statusPillClass(valor: string | null): string {
    switch (valor) {
      case 'TAP':
        return 'bg-[#FCF2E6] text-[#AA5B00]';
      case 'ACOLHIDA':
        return 'bg-[#E1FCEF] text-[#14804A]';
      case 'CANCELADA':
        return 'bg-[#FAF0F3] text-[#D12953]';
      case 'ANULADA':
        return 'bg-[#E9EDF5] text-[#5A6376]';
      case 'ARBITRAL':
      case 'EM ANÁLISE':
        return 'bg-[#F0F1FA] text-[#4F5AED]';
      default:
        return 'bg-gray-100 text-gray-600 dark:bg-zinc-800 dark:text-gray-300';
    }
  }

  /** Intervalo escolhido no filtro de período, como [início, fim] (fim no fim do dia). */
  private periodoFiltro(): [Date | null, Date | null] {
    const p = this.filterForm?.value?.periodo;
    if (!Array.isArray(p) || !p[0]) return [null, null];
    const ini = new Date(p[0]);
    ini.setHours(0, 0, 0, 0);
    const fim = p[1] ? new Date(p[1]) : new Date(p[0]);
    fim.setHours(23, 59, 59, 999);
    return [ini, fim];
  }

  protected temPeriodo(): boolean {
    return this.periodoFiltro()[0] != null;
  }

  private parseData(v: string | null | undefined): Date | null {
    if (!v) return null;
    const d = new Date(v.length <= 10 ? `${v}T00:00:00` : v);
    return isNaN(d.getTime()) ? null : d;
  }

  /**
   * Sem filtro de período: mostra a linha do tempo inteira.
   * Com período: reduz ao estado que estava vigente no fim do período
   * (o que valia na data filtrada) — um único valor.
   */
  protected linhaVisivel(entradas: LinhaTempo[] | null | undefined): LinhaTempo[] {
    const linha = entradas ?? [];
    const [, fim] = this.periodoFiltro();
    if (!fim || linha.length === 0) return linha;

    let vigente: LinhaTempo | null = null;
    for (const e of linha) {
      const d = this.parseData(e.data);
      if (d != null && d <= fim) vigente = e;
    }
    return [vigente ?? linha[0]];
  }

  private load() {
    this.loading = true;
    const params = new PageableParams();
    params.page = this.showPagination && this.rows ? Math.floor(this.first / this.rows) : 0;
    params.size = this.showPagination ? (this.rows || 10) : 200;
    if (this.lastSort) {
      params.sort = [`${this.lastSort.field},${this.lastSort.order === 1 ? 'asc' : 'desc'}`];
    }
    params.filters = this.buildFilters();

    this.service.get(params).subscribe({
      next: res => {
        this.dados = res.content ?? [];
        this.totalRecords = res.totalElements ?? 0;
        this.loading = false;
      },
      error: err => {
        console.error('[VerificarDados] erro ao carregar', err);
        this.messageService.add({severity: 'error', summary: 'Erro', detail: 'Erro ao carregar os dados.'});
        this.dados = [];
        this.totalRecords = 0;
        this.loading = false;
      },
    });
  }

  private buildFilters(): Record<string, unknown> {
    const v = this.filterForm.value;
    const f: Record<string, unknown> = {};

    // p-datepicker range: uma data só => [ini, null]. Trata como o dia exato (ini = fim),
    // pra "filtrei 28/08" trazer a base como estava em 28/08.
    const [ini, fim] = Array.isArray(v.periodo) ? v.periodo : [null, null];
    if (ini) {
      f['periodoInicio'] = DateUtils.toIsoDateOnly(ini);
      f['periodoFim'] = DateUtils.toIsoDateOnly(fim ?? ini);
    }

    if (v.statusLinhaTempo?.length) f['statusLinhaTempo'] = v.statusLinhaTempo;
    if (v.faseLinhaTempo?.length) f['faseLinhaTempo'] = v.faseLinhaTempo;
    if (v.superintendencia?.length) f['superintendencia'] = v.superintendencia;
    if (v.concessionaria?.length) f['concessionaria'] = v.concessionaria;
    if (v.numeroNotificacao?.trim()) f['numeroNotificacao'] = v.numeroNotificacao.trim();
    if (v.processo?.trim()) f['processo'] = v.processo.trim();

    return f;
  }

  // --- Exibição de Colunas ---

  protected isColumnVisible(field: string): boolean {
    return this.visibleFields.has(field);
  }

  protected get visibleColumnsCount(): number {
    return this.visibleFields.size;
  }

  /** Nº NOT/Nº SEI/Concessionária/Superint./Data NOT contam 1 coluna física; Status e Fase
   * contam 2 (par datas+valores) — usado pro colspan das mensagens de vazio/carregando. */
  protected get totalPhysicalColumns(): number {
    let total = 0;
    for (const c of this.allColumns) {
      if (!this.visibleFields.has(c.field)) continue;
      total += (c.field === 'statusLinhaTempo' || c.field === 'faseLinhaTempo') ? 2 : 1;
    }
    return total || 1;
  }

  protected get skeletonColumns(): number[] {
    return Array.from({length: this.totalPhysicalColumns});
  }

  protected onOpenColumnsConfig() {
    this.configColumns = this.allColumns.map(c => ({...c, visible: this.visibleFields.has(c.field)}));
    this.showColumnsDialog = true;
  }

  protected showAllColumns() {
    this.configColumns.forEach(c => c.visible = true);
  }

  protected hideAllColumns() {
    this.configColumns.forEach(c => c.visible = false);
  }

  protected resetColumnsConfig() {
    this.configColumns = this.allColumns.map(c => ({...c, visible: true}));
  }

  protected applyColumnsConfig() {
    this.visibleFields = new Set(this.configColumns.filter(c => c.visible).map(c => c.field));
    this.showColumnsDialog = false;
    this.savePreferencias();
  }

  // --- Exibir Paginação / filtros por coluna ---

  protected onChangeShowPagination() {
    this.first = 0;
    this.reload$.next();
    this.savePreferencias();
  }

  protected toggleColumnFilters() {
    this.showColumnFilters = !this.showColumnFilters;
    this.savePreferencias();
  }

  private savePreferencias() {
    try {
      localStorage.setItem('historico_preferencias', JSON.stringify({
        visibleFields: [...this.visibleFields],
        showColumnFilters: this.showColumnFilters,
        showPagination: this.showPagination,
      }));
    } catch {
    }
  }

  private loadPreferencias() {
    try {
      const saved = localStorage.getItem('historico_preferencias');
      if (!saved) return;
      const prefs = JSON.parse(saved);
      if (Array.isArray(prefs.visibleFields) && prefs.visibleFields.length > 0) {
        this.visibleFields = new Set(prefs.visibleFields);
      }
      if (typeof prefs.showColumnFilters === 'boolean') this.showColumnFilters = prefs.showColumnFilters;
      if (typeof prefs.showPagination === 'boolean') this.showPagination = prefs.showPagination;
    } catch {
    }
  }
}
